import { z } from "zod";

import {
  PROGRESS_BASELINE_SOURCE,
  PROGRESS_BETTER_DIRECTION,
  PROGRESS_CHART_KIND,
  PROGRESS_DATA_STATE,
  PROGRESS_OUTCOME_COUNT_BASIS,
  PROGRESS_SEVERITY_BAND,
  PROGRESS_STATUS_CODE,
  PROGRESS_STATUS_FALLBACK,
  PROGRESS_STATUS_TONE,
} from "@/domains/progress/models/constants";

const progressChartKindSchema = z.enum(PROGRESS_CHART_KIND).catch(PROGRESS_CHART_KIND.UNKNOWN);

const progressStatusCodeSchema = z.enum(PROGRESS_STATUS_CODE).catch(PROGRESS_STATUS_CODE.UNKNOWN);

const progressStatusToneSchema = z.enum(PROGRESS_STATUS_TONE).catch(PROGRESS_STATUS_TONE.UNKNOWN);

const progressBetterDirectionSchema = z
  .enum(PROGRESS_BETTER_DIRECTION)
  .catch(PROGRESS_BETTER_DIRECTION.UNKNOWN);

const progressDataStateSchema = z.enum(PROGRESS_DATA_STATE).catch(PROGRESS_DATA_STATE.UNKNOWN);

const progressBaselineSourceSchema = z
  .enum(PROGRESS_BASELINE_SOURCE)
  .catch(PROGRESS_BASELINE_SOURCE.UNKNOWN);

const progressOutcomeCountBasisSchema = z
  .enum(PROGRESS_OUTCOME_COUNT_BASIS)
  .catch(PROGRESS_OUTCOME_COUNT_BASIS.UNKNOWN);

/** `.nullable()` before `.catch(null)`: the other order would swallow a legitimate `null`. */
const progressSeverityBandSchema = z.enum(PROGRESS_SEVERITY_BAND).nullable().catch(null);

export const progressSymptomSchema = z.object({
  code: z.string(),
  label: z.string(),
});

/** `value` is null when the check-in did not answer; an absent point means it never asked. */
export const progressPointSchema = z.object({
  checkInId: z.number(),
  recordedAt: z.string(),
  value: z.number().nullable(),
});

export const progressBaselineSchema = z.object({
  value: z.number(),
  recordedAt: z.string(),
  source: progressBaselineSourceSchema,
});

export const progressScaleSchema = z.object({
  min: z.number(),
  max: z.number(),
  step: z.number(),
  betterDirection: progressBetterDirectionSchema,
});

const progressStatusLabelSchema = z
  .string()
  .refine((label) => {
    return label.trim() !== "";
  })
  .nullable()
  .catch(null);

export const progressStatusSchema = z
  .object({
    code: z.string().pipe(progressStatusCodeSchema),
    label: progressStatusLabelSchema,
    tone: progressStatusToneSchema,
  })
  .transform(({ code, label, tone }, ctx) => {
    const fallback = code === PROGRESS_STATUS_CODE.UNKNOWN ? null : PROGRESS_STATUS_FALLBACK[code];
    const resolvedLabel = label ?? fallback?.label ?? null;

    if (resolvedLabel === null) {
      ctx.addIssue({ code: "custom", message: "A status of unknown code needs its own label" });

      return z.NEVER;
    }

    return {
      code,
      label: resolvedLabel,
      tone: tone === PROGRESS_STATUS_TONE.UNKNOWN && fallback !== null ? fallback.tone : tone,
    };
  });

/** Needs only the baseline; null unless the comparison group cleared its floor of 20 patients. */
export const progressIdealLineSchema = z.object({
  baseline: z.number(),
  /** Where the patient started, even when the group was widened past that band. */
  severityBand: progressSeverityBandSchema,
  medianImprovement: z.number(),
  idealScore: z.number(),
  horizonAt: z.string(),
  patientCount: z.number().int(),
  severityMatched: z.boolean(),
  /**
   * Deliberately not rendered: a per-chart cohort cannot be described once per page. Kept
   * on the contract, so it degrades rather than taking the page down if the server drops it.
   */
  caption: z.string().catch(""),
});

export const progressChartSchema = z.object({
  key: z.string(),
  kind: progressChartKindSchema,
  title: z.string(),
  subCaption: z.string(),
  scale: progressScaleSchema,
  /** `null` where the readings fluctuated too much to name a trend: render no tag at all. */
  status: progressStatusSchema.nullable().catch(null),
  dataState: progressDataStateSchema,
  baseline: progressBaselineSchema.nullable(),
  latest: progressPointSchema.nullable(),
  points: z.array(progressPointSchema),
  changeFromBaseline: z.number().nullable().catch(null),
  /** A line we cannot parse is a line we do not draw — `null` is already a rendered state. */
  idealLine: progressIdealLineSchema.nullable().catch(null),
  symptoms: z.array(progressSymptomSchema),
});

export const progressOutcomeItemSchema = z.object({
  key: z.string(),
  label: z.string(),
  count: z.number().int(),
  countBasis: progressOutcomeCountBasisSchema,
  checkInsAnswered: z.number().int(),
});

export const progressOutcomesSchema = z.object({
  applicable: z.boolean(),
  items: z.array(progressOutcomeItemSchema),
});

export const progressSideEffectProductSchema = z.object({
  id: z.number(),
  name: z.string(),
  role: z.string(),
});

export const progressSideEffectEntrySchema = z.object({
  id: z.string(),
  effect: z.string(),
  effectCode: z.string(),
  reportedAt: z.string(),
  product: progressSideEffectProductSchema.nullable(),
  actionText: z.string().nullable(),
});

/** `entries` arrive newest first and are rendered in payload order. */
export const progressSideEffectsSchema = z.object({
  applicable: z.boolean(),
  entries: z.array(progressSideEffectEntrySchema),
});

/** What the plan's EO member discount is worth; `null` where it cannot be totalled from the plan. */
export const progressPotentialDiscountSchema = z.object({
  /** Whole dollars, already floored server-side so the figure can never overstate the discount. */
  amountUsd: z.number().int().positive(),
  note: z.string(),
});

export const progressThankYouSchema = z.object({
  applicable: z.boolean(),
  checkInCount: z.number().int().nullable().catch(null),
  message: z.string().nullable(),
  potentialDiscount: progressPotentialDiscountSchema.nullable().catch(null),
});

export const progressIntroSchema = z.object({
  text: z.string(),
});

export const progressResponseSchema = z.object({
  /** Unread today: the handle a later contract would be branched on, so it stays on the payload. */
  schemaVersion: z.number().int().nullable().catch(null),
  isNewPatient: z.boolean(),
  generatedAt: z.string().nullable().catch(null),
  /** `.catch(null)`: a misshapen intro costs this one paragraph, never the page. */
  intro: progressIntroSchema.nullish().catch(null),
  goals: z.array(progressChartSchema),
  symptomGroups: z.array(progressChartSchema),
  qualityOfLife: progressChartSchema.nullable(),
  unscoredSymptoms: z.array(progressSymptomSchema),
  outcomes: progressOutcomesSchema,
  sideEffects: progressSideEffectsSchema,
  thankYou: progressThankYouSchema,
});

/** Rides along on `GET /webapp/careplans`, not on the Progress payload itself. */
export const progressTeaserSchema = z.object({
  body: z.string(),
});
